import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { emailTemplateService } from '@/services/emailTemplateService';
import type { EmailTemplate, TemplateVersion, VersionDiff } from '@/types/WeeklyReport';
import { format, formatDistanceToNow } from 'date-fns';
import { 
    Clock, 
    GitBranch, 
    History, 
    Loader2, 
    Pin, 
    PinOff, 
    RotateCcw,
    Eye,
    ChevronRight,
    User,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';

interface VersionHistoryPanelProps {
    template: EmailTemplate | null;
    isOpen: boolean;
    onClose: () => void;
    onRevert: (template: EmailTemplate) => void;
}

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
    template,
    isOpen,
    onClose,
    onRevert,
}) => {
    const { toast } = useToast();
    const [versions, setVersions] = useState<TemplateVersion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [diff, setDiff] = useState<VersionDiff | null>(null);
    const [isLoadingDiff, setIsLoadingDiff] = useState(false);
    const [isReverting, setIsReverting] = useState(false);
    const [isPinning, setIsPinning] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && template) {
            loadVersions();
        }
    }, [isOpen, template]);

    const loadVersions = async () => {
        if (!template) return;

        setIsLoading(true);
        try {
            const templateVersions = await emailTemplateService.getTemplateVersions(template.id);
            setVersions(templateVersions.sort((a, b) => b.versionNumber - a.versionNumber));
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to load version history',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVersionClick = (versionId: string) => {
        if (selectedVersions.includes(versionId)) {
            setSelectedVersions(selectedVersions.filter(id => id !== versionId));
        } else if (selectedVersions.length < 2) {
            setSelectedVersions([...selectedVersions, versionId]);
        } else {
            // Replace the first selected with the new one
            setSelectedVersions([selectedVersions[1], versionId]);
        }
    };

    const handleCompare = async () => {
        if (selectedVersions.length !== 2 || !template) return;

        setIsLoadingDiff(true);
        try {
            const diffResult = await emailTemplateService.getVersionDiff(
                template.id,
                selectedVersions[0],
                selectedVersions[1]
            );
            setDiff(diffResult);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to compare versions',
                variant: 'destructive',
            });
        } finally {
            setIsLoadingDiff(false);
        }
    };

    const handleRevert = async (versionId: string) => {
        if (!template) return;

        setIsReverting(true);
        try {
            const revertedTemplate = await emailTemplateService.revertToVersion(template.id, versionId);
            if (revertedTemplate) {
                toast({
                    title: 'Version restored',
                    description: 'Template has been reverted to the selected version',
                });
                onRevert(revertedTemplate);
                loadVersions();
            } else {
                throw new Error('Failed to revert');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to restore version',
                variant: 'destructive',
            });
        } finally {
            setIsReverting(false);
        }
    };

    const handleTogglePin = async (version: TemplateVersion) => {
        if (!template) return;

        setIsPinning(version.versionId);
        try {
            const success = version.isPinned
                ? await emailTemplateService.unpinVersion(template.id, version.versionId)
                : await emailTemplateService.pinVersion(template.id, version.versionId);

            if (success) {
                toast({
                    title: version.isPinned ? 'Version unpinned' : 'Version pinned',
                    description: version.isPinned 
                        ? 'This version can now be auto-pruned'
                        : 'This version will be preserved',
                });
                loadVersions();
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update pin status',
                variant: 'destructive',
            });
        } finally {
            setIsPinning(null);
        }
    };

    if (!template) return null;

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent className="w-[450px] sm:w-[540px] overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <History className="h-5 w-5" />
                        Version History
                    </SheetTitle>
                    <SheetDescription>
                        {template.name} • {versions.length} version(s)
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                    {/* Compare Button */}
                    {selectedVersions.length === 2 && (
                        <Button
                            onClick={handleCompare}
                            disabled={isLoadingDiff}
                            className="w-full"
                            variant="outline"
                        >
                            {isLoadingDiff ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <GitBranch className="h-4 w-4 mr-2" />
                            )}
                            Compare Selected Versions
                        </Button>
                    )}

                    {/* Diff View */}
                    {diff && (
                        <div className="p-4 rounded-lg bg-secondary/50 space-y-3">
                            <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm">
                                    v{diff.fromVersion} → v{diff.toVersion}
                                </h4>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDiff(null)}
                                >
                                    Close
                                </Button>
                            </div>
                            <Separator />
                            <div className="space-y-2 text-sm">
                                {diff.changes.length === 0 ? (
                                    <p className="text-muted-foreground">No changes between versions</p>
                                ) : (
                                    diff.changes.map((change, index) => (
                                        <div key={index} className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant={
                                                        change.type === 'added' ? 'default' :
                                                        change.type === 'removed' ? 'destructive' : 'secondary'
                                                    }
                                                    className="text-xs"
                                                >
                                                    {change.type}
                                                </Badge>
                                                <span className="font-mono text-xs">{change.field}</span>
                                            </div>
                                            {change.type !== 'added' && change.oldValue !== undefined && (
                                                <div className="pl-4 text-xs text-red-500 dark:text-red-400 font-mono bg-red-50 dark:bg-red-900/20 p-1 rounded">
                                                    - {JSON.stringify(change.oldValue, null, 2)}
                                                </div>
                                            )}
                                            {change.type !== 'removed' && change.newValue !== undefined && (
                                                <div className="pl-4 text-xs text-green-500 dark:text-green-400 font-mono bg-green-50 dark:bg-green-900/20 p-1 rounded">
                                                    + {JSON.stringify(change.newValue, null, 2)}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Version Timeline */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No version history available
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {versions.map((version, index) => {
                                const isSelected = selectedVersions.includes(version.versionId);
                                const isCurrent = version.versionNumber === template.currentVersion;

                                return (
                                    <div
                                        key={version.versionId}
                                        className={`relative p-3 rounded-lg border transition-all cursor-pointer ${
                                            isSelected 
                                                ? 'border-primary bg-primary/5' 
                                                : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                                        }`}
                                        onClick={() => handleVersionClick(version.versionId)}
                                    >
                                        {/* Timeline connector */}
                                        {index < versions.length - 1 && (
                                            <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-border -mb-2 z-0" />
                                        )}

                                        <div className="flex items-start gap-3 relative z-10">
                                            {/* Version indicator */}
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                                isCurrent 
                                                    ? 'bg-primary text-primary-foreground' 
                                                    : 'bg-secondary text-secondary-foreground'
                                            }`}>
                                                {version.versionNumber}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium text-sm">
                                                        Version {version.versionNumber}
                                                    </span>
                                                    {isCurrent && (
                                                        <Badge variant="default" className="text-xs">
                                                            Current
                                                        </Badge>
                                                    )}
                                                    {version.isPinned && (
                                                        <Badge variant="outline" className="text-xs gap-1">
                                                            <Pin className="h-3 w-3" />
                                                            Pinned
                                                        </Badge>
                                                    )}
                                                </div>

                                                {version.changeDescription && (
                                                    <p className="text-sm text-muted-foreground mt-1 truncate">
                                                        {version.changeDescription}
                                                    </p>
                                                )}

                                                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <User className="h-3 w-3" />
                                                        {version.createdBy?.split('@')[0] || 'Unknown'}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => handleTogglePin(version)}
                                                    disabled={isPinning === version.versionId}
                                                    title={version.isPinned ? 'Unpin version' : 'Pin version'}
                                                >
                                                    {isPinning === version.versionId ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : version.isPinned ? (
                                                        <PinOff className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <Pin className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                                {!isCurrent && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        onClick={() => handleRevert(version.versionId)}
                                                        disabled={isReverting}
                                                        title="Restore this version"
                                                    >
                                                        {isReverting ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <RotateCcw className="h-3.5 w-3.5" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default VersionHistoryPanel;

